import React, { useEffect, useContext, useState } from "react";
import { Redirect, Switch, Route as BaseRoute } from "react-router-dom";
import EmployeeContext from "contexts/EmployeeContext";
import { useOrganisationContext } from "./hooks/useOrganisationContext";
import {
  LandingPage,
  SettingsAll,
  Users,
  Profile,
  ProfilePage,
  Settings,
  InvoiceSettings,
  PasswordReset,
  GlobalSettings,
  CustomOrganisationSettings,
  VerifyEmail,
  Organisations,
  CustomerInvoices,
  Subscription,
  Xero,
  PrivacyPolicy,
  TermsConditions,
  Contacts,
  ContactGroups,
  Connect,
  Support,
  FAQ,
  Payroll,
  SelectionComponents,
  PayrollHrNew,
  Dashboard,
  UsersPage,
  UserList,
  UserGroups,
  Applications,
  Core,
  TaskManagementDragDrop,
  Announcement,
  ConnectToXeroGuide,
  ListTracking
} from "./pages";
import { DeveloperPortal } from "portal";
import { Route } from "./Route";
import { useClient, useUserContext } from "./hooks";
import { zid } from './integrations';
import { all, any } from './Helpers'
import { accountingPerms, applicationPerms, userAndSubsPerms } from "pages/PermissionsList";
import { SignUpWithXeroCallback, SignUpWithXeroRedirect } from "./features/sign-up-with-xero";

const AdminRoute = (props) => {
  const { isAdmin } = useUserContext();

  return <Route {...props} checkUser checkSuspended test={isAdmin} />;
};
const PublicRoute = (props) => <Route {...props} />;

const UserRoute = (props) => (
  <Route checkUser checkTrial checkSuspended {...props} />
);

const OrganisationRoute = (props) => {
  return <UserRoute checkOrganisation {...props} />;
};

const DeveloperRoute = (props) => (
  <Route checkUser {...props} />
);

const Routes = (props) => {
  const {
    isAdmin,
    isUser,
    isAuthenticated,
    isInTrialPeriod,
  } = useUserContext();
  const { employee } = useContext(EmployeeContext);
  const { selectedOrganisation } = useOrganisationContext();

  const [subscribedFeatures, setSubscribedFeatures] = useState({
    isAccounting: isInTrialPeriod,
    isWPConnect: isInTrialPeriod,
    isPayroll: isInTrialPeriod,
    isActive: isInTrialPeriod,
  })

  const client = useClient();
  let redirect = null;

  useEffect(() => {
    if (!isInTrialPeriod && selectedOrganisation?.subscribed_features) {
      let subFeatures = selectedOrganisation?.subscribed_features
      let accounting = subFeatures?.hesaabmodule?.includes('accounting')
      let wpconnect = subFeatures?.hesaabmodule?.includes('wpconnect')
      let payroll = subFeatures?.hesaabmodule?.includes('payroll')
      let active = accounting || wpconnect || payroll

      setSubscribedFeatures({
        isAccounting: accounting,
        isWPConnect: wpconnect,
        isPayroll: payroll,
        isActive: active,
      })
    }

  }, [selectedOrganisation,])

  const {
    isAccounting,
    isWPConnect,
    isPayroll,
    isActive,
  } = subscribedFeatures

  if (isAdmin) {
    redirect = "/subscribers";
  } else if (isAccounting || isUser) {
    // redirect = "/accounts_receivable";
    redirect = "/home";
    //return <SelectionComponents/>
  } else if (isPayroll) {
    // redirect = "/payroll/pay_runs";
    redirect = "/home";

  } else if (isWPConnect) {
    // redirect = "/wp-connect";
    redirect = "/home";
  } else if (employee) {
    redirect = (employee.invitation_type === 'e-invoice' ? "/employee_einvoice" : "/employee_payroll");

  } else {
    redirect = "/";
  }

  const handleLogout = async (e) => {
    e.preventDefault()
    await client.post("logout")
    localStorage.clear();
  }

  return (
    <Switch>
      {/* Developer Portal Pages Start */}

      <PublicRoute exact path="/developers" render={(props) => (<DeveloperPortal.Landing {...props} isAuthenticated={isAuthenticated} />)} />
      <PublicRoute exact path="/developers/log-in" render={(props) => (<DeveloperPortal.Login {...props} isAuthenticated={isAuthenticated} />)} />
      <PublicRoute exact path="/developers/docs" render={(props) => (<DeveloperPortal.Docs {...props} isAuthenticated={isAuthenticated} />)} />
      <PublicRoute exact path="/developers/register" render={(props) => (<DeveloperPortal.RegisterUser {...props} isAuthenticated={isAuthenticated} />)} />

      <DeveloperRoute exact path="/developers/apps" component={DeveloperPortal.Apps} />
      <DeveloperRoute exact path="/developers/apps/register/:id" component={DeveloperPortal.RegisterApp} />

      {/* Developer Portal Pages End */}

      <AdminRoute exact path="/admin-dashboard" component={Dashboard.SuperadminDash} />

      <AdminRoute exact path="/invoice_settings" component={InvoiceSettings} />
      <AdminRoute exact path="/global_settings" component={GlobalSettings} />
      <PublicRoute exact path="/sign_up_with_xero" component={SignUpWithXeroRedirect} />
      <PublicRoute exact path="/sign_up_with_xero/callback" component={SignUpWithXeroCallback} />
      <AdminRoute exact fluid path="/subscribers" component={Users.List} />
      <AdminRoute exact path="/subscribers/:id" component={Users.Edit} />
      <AdminRoute exact fluid path="/users" component={Users.UserSearch} />
      <AdminRoute exact path="/users/:id" component={Users.Edit} />
      <AdminRoute exact fluid path="/managegroups" component={UserGroups.List} />
      <AdminRoute exact fluid path="/managegroups/:id" component={UserGroups.Edit} />

      <AdminRoute fluid exact path="/subscription/manage" component={Subscription.Manage} />
      <AdminRoute fluid exact path="/subscriptions_settings" component={Subscription.Settings} />
      <AdminRoute fluid exact path="/core/company" component={Core.CompanyList} />
      <AdminRoute fluid exact path="/core/company/:id" component={Core.CompanyEdit} />
      <AdminRoute fluid exact path="/core/company-detail/:id" component={Core.CompanyDetail} />
      <AdminRoute fluid exact path="/core/organisation" component={Core.OrganisationList} />
      <AdminRoute fluid exact path="/core/organisation/:id" component={Core.OrganisationDetail} />
      <AdminRoute fluid exact path="/core/modules" component={Core.ModuleList} />
      <AdminRoute fluid exact path="/core/modules/:id" component={Core.ModuleEdit} />
      <AdminRoute fluid exact path="/core/app" component={Core.AppList} />
      <AdminRoute fluid exact path="/core/app/:id" component={Core.AppEdit} />
      <AdminRoute fluid exact path="/core/language" component={Core.LanguageList} />
      <AdminRoute fluid exact path="/core/language/:id" component={Core.LanguageEdit} />
      <AdminRoute fluid exact path="/core/promocode" component={Core.PromocodeList} />
      <AdminRoute fluid exact path="/core/promocode/:id" component={Core.PromocodeEdit} />
      <AdminRoute fluid exact path="/subscription/plan" component={Subscription.PlanList} />
      <AdminRoute fluid exact path="/subscription/plan/:id" component={Subscription.PlanEdit} />
      <AdminRoute exact path="/customer_invoices" component={CustomerInvoices.List} />
      <AdminRoute fluid exact path="/core/invoice/:id" component={Core.InvoiceView} />

      <AdminRoute exact path="/subscriptions/:id" component={Subscription.EditSubscriptionFeature} />

      <AdminRoute exact path="/announcements" component={Announcement.List} />
      <AdminRoute exact path="/announcements/new" component={Announcement.Edit} />
      <AdminRoute exact path="/announcements/:id" component={Announcement.Edit} />

      <UserRoute exact path="/subscription-summary/:id" component={Subscription.PaymentSummary} test={isAuthenticated} />
      <UserRoute exact path="/payment/callback/" component={Subscription.Callback} test={isAuthenticated} />

      <AdminRoute fluid exact path="/core/employees" component={Core.HesaabEmployeeList} />
      <AdminRoute fluid exact path="/core/employees/:id" component={Core.HesaabEmployeeEdit} />

      <AdminRoute exact path="/connect" component={Connect} />

      {!isAuthenticated && (
        <PublicRoute exact path="/" component={LandingPage} noContainer />
      )}
      {!isAuthenticated && (
        <PublicRoute exact path="/login/" render={(props) => (<LandingPage {...props} modalType="login" noContainer />)} />
      )}

      {isAuthenticated && (
        <PublicRoute exact path="/logout" render={(props) => { handleLogout() }} />
      )}
      <PublicRoute exact path="/auth/" render={(props) => (<Applications.Login {...props} isAuthenticated={isAuthenticated} />)} />
      <PublicRoute exact path="/auth/docs" render={(props) => (<Applications.Docs {...props} isAuthenticated={isAuthenticated} />)} />

      {!isAuthenticated && (
        <PublicRoute exact path="/register/" render={(props) => (<LandingPage {...props} client={client} modalType="register" noContainer />)} />
      )}

      <PublicRoute exact path="/support" component={Support} />
      <PublicRoute exact path="/faq" component={FAQ} />

      <Redirect exact from="/auth/register" to="/auth/register/new" />
      <Redirect exact from="/developers/apps/register" to="/developers/apps/register/new" />

      <PublicRoute
        exact
        path="/password_reset/:token"
        component={PasswordReset.PasswordReset}
      />
      <PublicRoute
        exact
        path="/forgotten_password"
        component={PasswordReset.PasswordResetRequest}
      />
      <PublicRoute exact path="/verify_email/:token" component={VerifyEmail} />
      <PublicRoute exact path="/privacy_policy" component={PrivacyPolicy} />
      <PublicRoute exact path="/Terms-Conditions" component={TermsConditions} />
      <PublicRoute exact path="/connect-xero" component={ConnectToXeroGuide} />
      <Route
        exact
        path="/change_password"
        authRequired
        component={Profile}
        test={isAuthenticated}
      />
      <Route
        exact
        path="/profile"
        authRequired
        component={ProfilePage}
        test={isAuthenticated}
      />

      <OrganisationRoute
        exact
        path="/organisation-stats"
        component={Dashboard.SuperadminDash}
        access={accountingPerms}
        test={isAuthenticated}
      />

      <OrganisationRoute
        exact
        path="/dashboard"
        component={Dashboard.Dash}
        test={isActive}
        access={['xero_account_dashboard']}
      />

      <OrganisationRoute
        exact
        path="/auth/register/:id"
        component={Applications.Register}
        test={isActive}
      />

      <OrganisationRoute
        exact
        path="/auth/apps"
        component={Applications.Apps}
        test={isActive}
      />

      <OrganisationRoute
        exact
        path="/charts/bankbalance"
        component={Dashboard.BankBalanceCash}
        test={isActive}
        access={['xero_balance_sheet']}
      />
      <OrganisationRoute
        exact
        path="/charts/profit_loss"
        component={Dashboard.ProfitAndLoss}
        test={isActive}
        access={['xero_profit_and_loss']}
      />
      <OrganisationRoute
        exact
        path="/charts/receivable_payable"
        component={Dashboard.Receivables}
        test={isActive}
        access={['xero_payable_receivable']}
      />
      <OrganisationRoute
        exact
        path="/charts/expenses"
        component={Dashboard.ExpensesTypes}
        test={isActive}
        access={['expense_report']}
      />
      <OrganisationRoute
        exact
        path="/charts/balancechart"
        component={Dashboard.BalanceChart}
        test={isActive}
        access={['xero_balance_sheet']}
      />
      <OrganisationRoute
        exact
        path="/payroll/dashboard"
        component={Dashboard.HR}
        test={isActive}
        access={['hr_payroll_dashboard']}
      />
      <UserRoute
        exact
        path="/xero_connect"
        component={Organisations.XeroConnect}
        test={isActive}
      />
      <UserRoute
        exact
        path="/organisations/:id"
        component={Organisations.Edit}
        test={isAuthenticated}
      />
      <UserRoute
        fluid
        exact
        path="/organisations"
        component={Organisations.List}
        test={isAuthenticated}
      />
      <UserRoute
        exact
        path="/allusers"
        checkSuspended={false}
        component={UsersPage.Home}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <UserRoute
        exact
        path="/userslist"
        checkSuspended={false}
        component={UserList.List}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <UserRoute
        exact
        path="/userslist/:id"
        checkSuspended={false}
        component={UserList.Edit}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <PublicRoute
        exact
        path="/authorization/invite/:token"
        component={UserList.InviteAuth}
      />
      <PublicRoute
        exact
        path="/authorization/success"
        component={UserList.Success}
      />
      <UserRoute
        exact
        path="/subscription"
        checkSuspended={false}
        component={Subscription.View}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <UserRoute
        exact
        path="/subscription/invoice/:id"
        checkSuspended={false}
        component={Subscription.ViewInvoice}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <UserRoute
        exact
        path="/change-subscription"
        checkSuspended={false}
        component={Subscription.ChangeSubscription}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <UserRoute
        exact
        path="/customize-subscription"
        checkSuspended={false}
        component={Subscription.CustomizeSubscription}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <UserRoute
        exact
        path="/feature-subscription"
        checkSuspended={false}
        component={Subscription.FeatureSubscription}
        test={isUser}
        access={userAndSubsPerms}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/applications"
        checkSuspended={false}
        component={Applications.List}
        test={isUser}
        access={applicationPerms}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/accounts_receivable/invoices/edit/:id"
        component={Xero.AccountsReceivable.EditInvoice}
        test={isAccounting}
        access={['xero_sales', 'xero_sales_edit']}
        func={all}

      />
      <UserRoute
        exact
        path="/accounts_receivable/invoices/view/:id"
        component={Xero.AccountsReceivable.ViewEInvoice}
        access={['xero_sales']}
        func={all}
      />
      <OrganisationRoute
        fluid
        path="/accounts_receivable/invoices"
        component={Xero.AccountsReceivable.Landing}
        test={isAccounting}
        access={['xero_sales']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/accounts_receivable/credit_notes/edit/:id"
        component={Xero.AccountsReceivable.EditCreditNote}
        test={isAccounting}
        access={['xero_sales', 'xero_sales_edit']}
      />

      <UserRoute
        exact
        path="/accounts_receivable/credit_notes/view/:id"
        component={Xero.AccountsReceivable.ViewCreditNote}
        access={['xero_sales']}
      />

      <OrganisationRoute
        exact
        path="/accounts_receivable/credit_notes/edit/:id/allocate"
        component={Xero.AccountsReceivable.AllocateCredit}
        test={isAccounting}
        access={['xero_sales', 'xero_sales_edit']}
      />
      <OrganisationRoute
        fluid
        path="/accounts_receivable/credit_notes"
        component={Xero.AccountsReceivable.Landing}
        test={isAccounting}
        access={['xero_sales']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/accounts_receivable/repeating/edit/:id"
        component={Xero.AccountsReceivable.EditRepeating}
        test={isAccounting}
        access={['xero_sales', 'xero_sales_edit']}
        func={all}
      />
      <UserRoute
        exact
        path="/accounts_receivable/repeating/view/:id"
        component={Xero.AccountsReceivable.ViewRepeating}
        access={['xero_sales', 'xero_purchases', 'xero_sales_edit', 'xero_purchases_edit']}
        func={any}
      />

      <OrganisationRoute
        fluid
        path="/accounts_receivable/repeating"
        component={Xero.AccountsReceivable.Landing}
        test={isAccounting}
        access={['xero_sales']}
        func={all}
      />
      <OrganisationRoute
        fluid
        path="/accounts_receivable"
        component={Xero.AccountsReceivable.Landing}
        test={isAccounting}
        access={['xero_sales']}
        func={all}
      />
      <OrganisationRoute
        path="/batch-payments/view/:batch_payment_id"
        component={Xero.BatchPayment.ViewBatchPayment}
        test={isAccounting}
        access={['xero_sales']}
        func={all}
      />
      <OrganisationRoute
        path="/batch-payments/:id"
        component={Xero.BatchPayment.EditBatchPayment}
        test={isAccounting}
        access={['xero_sales']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/quotes/edit/:id"
        component={Xero.Quotes.Edit}
        test={isAccounting}
        access={["xero_quotes"]}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/quotes/view/:id"
        component={Xero.Quotes.View}
        test={isAccounting}
        access={['xero_quotes']}
        func={all}
      />
      <OrganisationRoute
        path="/quotes"
        component={Xero.Quotes.List}
        test={isAccounting}
        access={['xero_quotes']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/userspage"
        component={UsersPage.List}
        test={isAccounting}
        access={userAndSubsPerms}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/userspage/new"
        component={UsersPage.MyComponent}
        test={isAccounting}
        access={userAndSubsPerms}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/customers"
        component={Contacts.List}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/suppliers"
        component={Contacts.List}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/contacts"
        component={Contacts.List}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/contacts/:id/statement"
        component={Contacts.Statement}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/contacts/:id"
        component={Contacts.Edit}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/contact_groups"
        component={ContactGroups.List}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/contacts/view/:id"
        component={Contacts.View}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/contact_groups/:id"
        component={ContactGroups.Edit}
        test={isAccounting}
        access={['xero_contacts']}
      />
      <OrganisationRoute
        exact
        path="/reports/trial_balance"
        component={Xero.Reports.TrailBalanceWrapper}
        test={isAccounting}
        access={['xero_trial_balance']}
      />
      <OrganisationRoute
        exact
        path="/reports/tb_with_movement"
        component={Xero.Reports.TrialBalanceWithMovementOld}
        test={isAccounting}
        access={['xero_trial_balance_movement']}
      />
      <OrganisationRoute
        exact
        path="/reports/tb_with_movement_new"
        component={Xero.Reports.TrialBalanceWithMovement}
        test={isAccounting}
        access={['xero_trial_balance_movement']}
      />
      <OrganisationRoute
        exact
        path="/reports/tb_with_movement_summary"
        component={Xero.Reports.TrialBalanceWithMovementBySummary}
        test={isAccounting}
        access={['xero_trial_balance_movement']}
      />

      <OrganisationRoute
        exact
        path="/reports/bank_summary"
        component={Xero.Reports.BankSummary}
        test={isAccounting}
        access={['xero_bank_summary']}
      />
      <OrganisationRoute
        exact
        path="/reports/executive_summary"
        component={Xero.Reports.ExecutiveSummary}
        test={isAccounting}
        access={['xero_executive_summary']}
      />
      <OrganisationRoute
        exact
        path="/reports/budget_summary"
        component={Xero.Reports.BudgetSummary}
        test={isAccounting}
        access={['xero_budget_summary']}
      />
      <OrganisationRoute
        exact
        path="/reports/profit_and_loss"
        component={Xero.Reports.ProfitAndLoss}
        test={isAccounting}
        access={['xero_profit_and_loss']}
      />
      <OrganisationRoute
        exact
        path="/reports/payables_receivables"
        component={Xero.Reports.AgedByContact}
        test={isAccounting}
        access={['xero_payable_receivable']}
      />
      <OrganisationRoute
        exact
        path="/reports/zatca"
        component={Xero.Reports.ZatcaReport}
        test={isAccounting}
        access={['zatca_tax_report']}
      />
      <OrganisationRoute
        exact
        path="/reports/reported_documents"
        component={Xero.Reports.ReportedDocuments}
        test={isAccounting}
        access={['zatca_tax_report']}
      />
      <OrganisationRoute
        exact
        path="/reports/payable_receivable/detail" //Make this dynamic by adding report type after slash
        component={Xero.Reports.PayableReceivable}
        test={isAccounting}
      />
      <OrganisationRoute
        exact
        path="/reports/contact_transactions"
        component={Xero.Reports.ContactTransactions}
        test={isAccounting}
      />
      <OrganisationRoute
        exact
        path="/reports/aged_payables_by_contact/:id"
        component={Xero.Reports.AgedPayablesByContact}
        test={isAccounting}
        access={['xero_payable_receivable']}
      />
      <OrganisationRoute
        exact
        path="/reports/aged_receivables_by_contact/:id"
        component={Xero.Reports.AgedReceivablesByContact}
        test={isAccounting}
        access={['xero_payable_receivable']}
      />
      <OrganisationRoute
        exact
        path="/reports/balance_sheet"
        component={Xero.Reports.BalanceSheet}
        test={isAccounting}
        access={['xero_balance_sheet']}
      />
      <OrganisationRoute
        exact
        path="/reports/bank_statement/:id"
        component={Xero.Reports.BankStatement}
        test={isAccounting}
        access={['xero_bank_summary']}
      />
      <OrganisationRoute
        exact
        path="/reports/audit_reports"
        component={Xero.AuditReports.AuditFile}
        test={isAccounting}
        access={['xero_reports']}
      />
      <OrganisationRoute
        exact
        path="/reports/audit_reports/:id"
        component={Xero.AuditReports.AddNewYear}
        test={isAccounting}
        access={['xero_reports']}
      />
      <OrganisationRoute
        exact
        path="/reports/audit_reports/audit_files/:id"
        component={Xero.AuditReports.AuditReportsFiles}
        test={isAccounting}
        access={['xero_reports']}
      />
      <OrganisationRoute
        exact
        path="/reports/audit_reports/master_file/:id/:master_file_id"
        component={Xero.AuditReports.AddMasterFiles}
        test={isAccounting}
        access={['xero_reports']}
      />
      <OrganisationRoute
        exact
        path="/reports/audit_reports/audit_requirements/:id/:audit_requirements_id"
        component={Xero.AuditReports.AuditRequirements}
        test={isAccounting}
        access={['xero_reports']}
      />
      <OrganisationRoute
        exact
        path="/reports/audit_reports/request_during_audit/:id/:request_during_audit_id"
        component={Xero.AuditReports.RequestDuringAudit}
        test={isAccounting}
        access={['xero_reports']}
      />
      <OrganisationRoute
        exact
        path="/reports/audit_reports/report_summary/:id/:summary_id"
        component={Xero.AuditReports.ReportSummary}
        test={isAccounting}
        access={['xero_reports']}
      />
      <OrganisationRoute
        exact
        path="/reports/tracks"
        component={Payroll.Tracks.Reports}
        test={isAccounting}
        access={['payroll_payment_report']}
      />
      <OrganisationRoute
        exact
        path="/reports/sales_tax"
        component={Xero.Reports.SalesTaxReport}
        test={isAccounting}
        access={['xero_sales_tax']}
      />
      <OrganisationRoute
        path="/accounts/chart_of_accounts/:klass"
        component={Xero.Settings.ChartOfAccounts}
        test={isAccounting}
        access={['xero_chart-of-accounts', 'xero_accounts']}
        func={any}
      />
      <OrganisationRoute
        path="/codes"
        component={Xero.Settings.Codes}
        test={isAccounting}
      />
      <OrganisationRoute
        path="/tax_details"
        component={Xero.Settings.TaxReturnsDetail}
        test={isAccounting}
        access={['xero_accounts', 'xero_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/accounts/manual_journals/:status"
        component={Xero.Settings.ManualJournalsList}
        access={['xero_accounts', 'xero_manual-journal_edit']}
        test={isAccounting}
        func={any}
      />
      <OrganisationRoute
        exact
        path="/accounts/manual_journals/:status/:id"
        component={Xero.Settings.ManualJournalsEdit}
        test={isAccounting}
        access={['xero_manual-journal_edit']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/bank/bank_accounts/:account_id/bank_transactions/transfer"
        component={Xero.Bank.BankTransfer}
        test={isAccounting}
        access={['xero_bank-accounts', 'xero_accounts']}
        func={any}
      />
      <OrganisationRoute
        exact
        path="/bank/bank_accounts/:id/bank_transactions"
        component={Xero.Bank.BankTransactions}
        test={isAccounting}
        access={['xero_bank-accounts', 'xero_accounts']}
        func={any}
      />
      <OrganisationRoute
        exact
        path="/bank/bank_accounts/:account_id/bank_transactions/:type/:id"
        component={Xero.Bank.EditTransaction}
        test={isAccounting}
        access={['xero_bank-accounts', 'xero_accounts']}
        func={any}
      />
      <OrganisationRoute
        exact
        path="/bank/bank_accounts"
        component={Xero.Bank.BankAccounts}
        test={isAccounting}
        access={['xero_bank-accounts', 'xero_accounts']}
        func={any}
      />
      <OrganisationRoute
        exact
        path="/payments/:id"
        component={Xero.Bank.Payment}
        test={isAccounting}
        access={['xero_accounts', 'xero_payments']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/transactions/:id"
        component={Xero.Bank.Transaction}
        test={isAccounting}
        access={['xero_accounts', 'xero_payments']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/transactions/:transactionType/:docType/:id"
        component={Xero.Bank.Transactions.View}
        test={isAccounting}
        access={['xero_accounts', 'xero_payments']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/transactions/:transactionType/:docType/:id/allocate"
        component={Xero.Bank.Transactions.Allocate}
        test={isAccounting}
        access={['xero_accounts', 'xero_payments']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/overpayments/:id"
        component={Xero.Bank.Overpayments}
        test={isAccounting}
        access={['xero_accounts', 'xero_payments']}
        func={all}
      />
      <OrganisationRoute
        path="/purchases/purchase_orders/edit/:id"
        component={Xero.Purchases.PurchaseOrders.Edit}
        test={isAccounting}
        access={['xero_purchases', 'xero_purchases-orders_edit']}
        func={all}
      />
      <OrganisationRoute
        path="/purchases/purchase_orders/view/:id"
        component={Xero.Purchases.PurchaseOrders.View}
        test={isAccounting}
        access={['xero_purchases']}
        func={all}
      />
      <OrganisationRoute
        path="/purchases/purchase_orders"
        component={Xero.Purchases.Landing}
        test={isAccounting}
        access={['xero_purchases']}
      />
      <OrganisationRoute
        path="/purchases/bills/edit/:id"
        component={Xero.Purchases.Bills.Edit}
        test={isAccounting}
        access={['xero_purchases', 'xero_purchases_edit']}
        func={all}
      />
      <OrganisationRoute
        path="/purchases/bills/view/:id"
        component={Xero.Purchases.Bills.View}
        test={isAccounting}
        access={['xero_purchases']}
        func={all}
      />
      <OrganisationRoute
        fluid
        path="/purchases/bills"
        component={Xero.Purchases.Landing}
        test={isAccounting}
        access={['xero_purchases']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/purchases/credit_notes/edit/:id"
        component={Xero.AccountsReceivable.EditCreditNote}
        componentProps={{ payable: true }}
        test={isAccounting}
        access={['xero_purchases', 'xero_purchases_edit']}
      />
      <UserRoute
        exact
        path="/purchases/credit_notes/view/:id"
        component={Xero.AccountsReceivable.ViewCreditNote}
        access={['xero_purchases']}
      />
      <OrganisationRoute
        exact
        path="/purchases/credit_notes/edit/:id/allocate"
        component={Xero.AccountsReceivable.AllocateCredit}
        componentProps={{ payable: true }}
        test={isAccounting}
        access={['xero_purchases', 'xero_purchases_edit']}
      />
      <OrganisationRoute
        fluid
        path="/purchases/credit_notes"
        component={Xero.Purchases.Landing}
        componentProps={{ payable: true }}
        test={isAccounting}
        access={["xero_purchases"]}
      />
      <OrganisationRoute
        exact
        path="/purchases/repeating/edit/:id"
        component={Xero.AccountsReceivable.EditRepeating}
        componentProps={{ payable: true }}
        test={isAccounting}
        access={['xero_purchases', 'xero_purchases_edit']}
      />
      <UserRoute
        exact
        path="/purchases/repeating/view/:id"
        component={Xero.AccountsReceivable.ViewRepeating}
        access={['xero_sales', 'xero_purchases', 'xero_purchases_edit']}
        func={any}
      />
      <OrganisationRoute
        path="/purchases/repeating"
        fluid
        component={Xero.Purchases.Landing}
        componentProps={{ payable: true }}
        test={isAccounting}
        access={['xero_purchases', 'xero_purchases_edit']}
      />
      <OrganisationRoute
        fluid
        path="/purchases"
        component={Xero.Purchases.Landing}
        test={isAccounting}
        access={['xero_purchases']}
        func={all}
      />

      <OrganisationRoute
        path="/inventory"
        exact
        component={Xero.Inventory.InventoryList}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/fixed-assets"
        exact
        component={Xero.FixedAssets.FixedAssetList}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/fixed-assets/:id"
        exact
        component={Xero.FixedAssets.AddNewAsset}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/fixed-assets/edit/:id"
        exact
        component={Xero.FixedAssets.AddNewAsset}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/fixed-assets/detail/:id"
        exact
        component={Xero.FixedAssets.RegisteredAssetDetail}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/import-assets"
        exact
        component={Xero.FixedAssets.ImportAsset}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/asset-types-list"
        exact
        component={Xero.FixedAssets.AssetTypesList}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/expenses-list"
        exact
        component={Xero.Expenses.List}
        test={isAccounting}
      />
      <OrganisationRoute
        path="/inventory/:id"
        exact
        component={Xero.Inventory.EditInventory}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        path="/inventory/:id/adjustment"
        exact
        component={Xero.Inventory.AdjustInventory}
        test={isAccounting}
        access={['xero_inventory']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/accounting/settings/email_templates"
        component={Xero.Settings.EmailTemplates.List}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        exact
        path="/accounting/settings/email_templates/:id"
        component={Xero.Settings.EmailTemplates.Edit}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        exact
        path="/accounting/settings/tax_rates"
        component={Xero.Settings.TaxRates.List}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        exact
        path="/accounting/settings/tax_rates/:id"
        component={Xero.Settings.TaxRates.Edit}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        exact
        path="/accounting/settings"
        component={Xero.Settings.Landing}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        exact
        path="/settings"
        component={SettingsAll}
        test={isAccounting}
        access={['xero_settings', 'hr_settings']}
        func={any}
      />

      <OrganisationRoute
        exact
        path="/home"
        component={SelectionComponents}
        test={isUser}
      />
      <OrganisationRoute
        exact
        path="/payroll-new/overview"
        component={PayrollHrNew.Overview}
        test={isPayroll}
      />
      <OrganisationRoute
        exact
        path="/tasks/task-management"
        component={TaskManagementDragDrop.ManagerTasks}
      />

      <OrganisationRoute
        exact
        path="/tasks/portal/tasks_management"
        component={TaskManagementDragDrop.ManagerTasks}
      />

      <OrganisationRoute
        exact
        path="/tasks/settings"
        component={TaskManagementDragDrop.Settings}
        access={['hr_task_manager']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/tasks/groups"
        component={TaskManagementDragDrop.Groups}
        access={['hr_task_manager']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/tasks/labels"
        component={TaskManagementDragDrop.Labels}
        access={['hr_task_manager']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/tasks/re-assign-requests"
        component={TaskManagementDragDrop.ReAssignRequests}
        access={['hr_task_manager']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/settings"
        component={PayrollHrNew.SettingsManagement}
        test={isPayroll}
        access={['hr_settings']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/organization-info"
        component={PayrollHrNew.OrganizationInfo}
        test={isPayroll}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/allowance-rules"
        component={PayrollHrNew.AllowanceRules.List}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/end-of-service"
        component={PayrollHrNew.EndOfService.List}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />



      <OrganisationRoute
        path="/payroll-new/branches-departments"
        component={PayrollHrNew.BranchesDepartments.List}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/public-holidays"
        component={PayrollHrNew.PublicHolidays.List}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings', 'hr_timeoff']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/public-holidays/:id"
        component={PayrollHrNew.PublicHolidays.Edit}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings', 'hr_timeoff']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/deductions"
        component={PayrollHrNew.Deductions.DeductionsList}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll-new/deductions/:id"
        component={PayrollHrNew.Deductions.EditDeduction}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/end-of-service"
        component={PayrollHrNew.EndOfService}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/holidays-settings"
        component={PayrollHrNew.HolidaysSettings}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings', 'hr_timeoff']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/payrun-settings"
        component={PayrollHrNew.PayrunSettings}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings', 'hr_payruns']}
        func={all}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/document-type"
        component={PayrollHrNew.DocumentType}
        test={isPayroll}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/employee-type"
        component={PayrollHrNew.EmployeeType}
        test={isPayroll}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/shift"
        component={PayrollHrNew.ShiftsManagement.ShiftsManagementList}
        test={isPayroll}
        access={['hr_payroll']}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/shift/:id"
        component={PayrollHrNew.ShiftsManagement.Edit}
        test={isPayroll}
        access={['hr_payroll']}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/attendance-report"
        component={PayrollHrNew.AttendanceReport.List}
        test={isPayroll}
        access={['hr_payroll']}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/attendance-report/:id"
        component={PayrollHrNew.AttendanceReport.Edit}
        test={isPayroll}
        access={['hr_payroll']}
      />

      <OrganisationRoute
        exact
        path="/payroll-new/assignment-shift"
        component={PayrollHrNew.ShiftsManagement.ShiftAssignment}
        test={isPayroll}
        access={['hr_payroll']}
        func={all}
      />

      <OrganisationRoute
        path="/accounting/settings/tracking"
        component={Xero.Settings.TrackingCategories.List}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        path="/accounting/settings/currencies"
        component={Xero.Settings.Currencies}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        exact
        path="/accounting/settings/invoice_settings"
        component={Settings}
        test={isAccounting}
        access={['xero_settings']}
      />
      <OrganisationRoute
        exact
        path="/accounting/settings/custom_settings"
        component={CustomOrganisationSettings}
        test={isAccounting}
        access={['xero_settings']}
      />

      {/* Not used anymore */}
      {/* <OrganisationRoute
        exact
        path="/payroll/public_holidays"
        component={Payroll.Setup.PublicHolidays.List}
        test={isPayroll}
      />
      <OrganisationRoute
        exact
        path="/payroll/public_holidays/:id"
        component={Payroll.Setup.PublicHolidays.Edit}
        test={isPayroll}
      /> */}
      <UserRoute
        exact
        path="/payroll/time_off/accrued"
        component={Payroll.TimeOff.Accrued}
        access={['hr_timeoff']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/time_off/:id"
        component={Payroll.Employees.Holiday.Edit}
        test={isPayroll}
        access={['hr_timeoff']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/employees"
        component={Payroll.Employees.List}
        test={isPayroll}
        access={['hr_payroll']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/employees/:id"
        component={Payroll.Employees.Edit}
        test={isPayroll}
        access={['hr_payroll']}
      />
      <OrganisationRoute
        exact
        path="/payroll/attachments"
        component={Payroll.Employees.Attachment.List}
        test={isPayroll}
        access={['hr_payroll']}
      />
      <OrganisationRoute
        exact
        path="/payroll/settings"
        component={Payroll.Settings}
        test={isPayroll}
        access={['hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/loans"
        component={Payroll.Loans.List}
        test={isPayroll}
        access={['hr_loans']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/loans/:id"
        component={Payroll.Loans.Edit}
        test={isPayroll}
        access={['hr_loans']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll-new/contracts"
        component={Payroll.Contracts.List}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll-new/contracts/:id"
        component={Payroll.Contracts.Edit}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/pay_runs/:id"
        component={Payroll.PayRuns.Edit}
        test={isPayroll}
        access={['hr_payruns']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/pay_runs/:id/pay_slips/:pay_slip_id"
        component={Payroll.PayRuns.PaySlip}
        test={isPayroll}
        access={['hr_payruns']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/pay_runs"
        component={Payroll.PayRuns.List}
        test={isPayroll}
        access={['hr_payruns']}
      />
      <OrganisationRoute
        path="/payroll/tracks"
        component={Payroll.Tracks.Reports}
        test={isPayroll}
        access={['hr_payroll']}
      />
      <OrganisationRoute
        exact
        path="/payroll/provisions"
        component={Payroll.Provisions.ProvisionList}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/provisions/:id"
        component={Payroll.Provisions.EditProvision}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/tax_rules"
        component={Payroll.TaxRules.TaxRulesList}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/payroll/tax_rules/:id"
        component={Payroll.TaxRules.EditTaxRule}
        test={isPayroll}
        access={['hr_payroll', 'hr_settings']}
        func={all}
      />
      <OrganisationRoute
        exact
        path="/lists/list-tracking"
        component={ListTracking.Lists}
      />
      <OrganisationRoute
        exact
        path="/tracking-lists/detail/:id"
        component={ListTracking.ListDetails}
      />

      <PublicRoute
        exact
        path="/invite/:token/:invitation_type"
        component={Payroll.Employees.Invite}
      />
      <UserRoute
        exact
        path="/employee_payroll"
        component={Payroll.Portal.Portal}
      />
      <UserRoute
        exact
        path="/employee_einvoice"
        component={Payroll.Portal.EInvoicePortal}
      />
      <UserRoute
        exact
        path="/employee_payroll/contracts"
        component={Payroll.Portal.PContracts.List}
      />
      <UserRoute
        exact
        path="/employee_payroll/holidays"
        component={Payroll.Portal.PHolidays.List}
      />
      <UserRoute
        exact
        path="/employee_payroll/holidays/:id/request_holiday"
        component={Payroll.Portal.PHolidays.Edit}
      />
      <UserRoute
        exact
        path="/employee_payroll/loans"
        component={Payroll.Portal.PLoans.List}
      />
      <UserRoute
        exact
        path="/employee_payroll/loans/:id/request_loan"
        component={Payroll.Portal.PLoans.Edit}
      />

      <UserRoute
        exact
        path="/employee_payroll/:id/iqama-renewal-requests/:request_id"
        component={Payroll.Portal.IqamaRenewalRequests.Edit}
      />

      <UserRoute
        exact
        path="/payroll/time_off"
        component={Payroll.TimeOff.TimeOff}
        access={['hr_timeoff']}
        func={all}
      />
      <PublicRoute
        exact
        path="/auth"
        component={Applications.Login}
      />
      <PublicRoute
        exact
        path="/callbacks/:app"
        component={Applications.CallBackHandler}
      />
      <UserRoute
        exact
        path="/zatca/details"
        component={Applications.ZatcaDetails}
      />
      <UserRoute
        exact
        path="/zatca/details/:id"
        component={Applications.ZatcaDetails}
      />
      <UserRoute
        exact
        path="/zatca/registration"
        component={Applications.RegistrationSDK}
      />
      <UserRoute
        exact
        path="/zatca/renew"
        component={Applications.Renew}
      />
      <UserRoute
        exact
        path="/zatca/renew/:id"
        component={Applications.Renew}
      />
      <UserRoute
        exact
        path="/zatca/registration/sdk"
        component={Applications.RegistrationSDK}
      />
      <BaseRoute
        exact
        path="/e-invoicing"
        component={Applications.ZatcaHome}>
      </BaseRoute>
      {/* <BaseRoute path="/wp-connect">
        <WPConnect.Home />
      </BaseRoute> */}
      <PublicRoute path="/api" noContainer />
      <UserRoute path="/zid" component={zid.Routes} />
      <UserRoute
        exact
        path="/omega/credential"
        component={Applications.OmegaCredential}
      />
      {redirect ? <Redirect to={redirect} /> : null}
    </Switch>
  );
};

export default Routes;
